import { T, UI, SPACE } from "../constants/tokens";

// ─── The shape of a card ─────────────────────────────────────────────────────
// Exported so the containers that cannot become a <Card> — ones that need a
// header/body split, their own scroll region, or a per-row border colour — can
// still be drawn with the same corner and the same gutter. 99 such containers
// were hand-rolled against 13 uses of this component, and they had drifted to
// six radii and eighteen padding pairs between them.
export const CARD_PAD = "22px 22px 24px";
// Borderless since 2026-09-23 (David, after a phone review): the hairline
// frame around every card read as a faint box rather than as a surface, and on
// sand it was nearly invisible anyway. A card is now told apart from the page
// by its ground and its lift — see the --si-surface-card note in index.css.
export const CARD_SHAPE = {
  background: UI.surface.card,
  borderRadius: T.radius.xl,
  boxShadow: UI.shadow.cardEl,
};

// The "go there" link at the foot of a home card. Text with a directional
// arrow, never a boxed button: the home screen keeps its emphasis on the
// numbers, and a row of competing boxes would take it away from them.
//
// It is a link, so it is drawn in `interactive`, not in the gold: the home
// screen carries three of these at once, and gold is reserved for AI insight,
// Pro and the one primary action per screen.
//
// It lives here rather than in HomeTab because the cards that use it are now
// in separate files, and importing it back from HomeTab would be a cycle.
export const CARD_ACTION = {
  background: "transparent",
  border: "none",
  padding: "8px 0 0",
  minHeight: 44,
  fontSize: 14,
  fontWeight: 700,
  color: UI.interactive,
  cursor: "pointer",
  fontFamily: "inherit",
  marginTop: 4,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

export function Card({ children, style, onClick, ...rest }) {
  return (
    <div onClick={onClick} {...rest}
      className={`si-card${onClick ? " si-card-click" : ""}`}
      style={{
        background: UI.surface.card,
        boxShadow: UI.shadow.cardEl,
        // --r-xl. The card radius from the spec's own scale; this was
        // T.radius.md, which is the BUTTON step, so cards were drawn with a
        // button's corner and the card step had no consumers at all.
        borderRadius: T.radius.xl,
        // The spec's card padding is 22/24, not 16 — 16 is a row's padding.
        padding: CARD_PAD,
        cursor: onClick ? "pointer" : undefined,
        ...style,
      }}>
      {children}
    </div>
  );
}

export function Pill({ label, color, bg, icon }) {
  return (
    <span className="si-badge" style={{ background: bg, color, border: `1px solid ${color}28` }}>
      {icon && <span style={{ display: "flex", alignItems: "center" }}>{icon}</span>}
      {label}
    </span>
  );
}

export function Toggle({ value, onChange, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${SPACE.sm}px 0` }}>
      <span style={{ fontSize: 14, color: UI.text.primary }}>{label}</span>
      <div onClick={() => onChange(!value)} style={{
        width: 44, height: 24, borderRadius: 999, cursor: "pointer",
        background: value ? UI.text.secondary : UI.fill.subtle,
        position: "relative", transition: "background .2s",
        boxShadow: "none",
      }}>
        <div style={{
          position: "absolute", top: 3,
          left: value ? 22 : 3,
          width: 18, height: 18, borderRadius: "50%",
          background: UI.text.primary,
          boxShadow: T.shadow,
          transition: "left .2s cubic-bezier(.4,0,.2,1)",
        }}/>
      </div>
    </div>
  );
}
